import React, { FunctionComponent } from 'react';
import { Grid, Tooltip, Typography, AnalyticsProps } from '@inplayer-org/inplayer-ui';
import { Field, FieldProps } from 'formik';
import styled from 'styled-components';
import { MdInfoOutline } from 'react-icons/md';
import { useSelector } from 'react-redux';

// components
import Input from 'components/Fields/Input';
import Select from 'components/Fields/Select';
import FieldError from 'components/Fields/FieldError';
import ClipBoardButton from 'components/ClipBoardButton';
import StyledContainer from 'components/StyledContainer';
import SingleDayPicker from 'components/SingleDayPicker';
import TimeHourPicker from 'components/TimeHourPicker';
import CheckboxField from 'components/Fields/CheckboxField';
import Restricted from 'restrictions/roles/components/Restricted';
import SeasonSubscriptionInfo from 'pages/MerchantPages/Assets/Price/SeasonSubscription/SeasonSubscriptionInfo';

// utils
import accessCurrencies from 'data/accessCurrencies';
import isEmpty from 'lodash/isEmpty';

// styled components
import { BlueTypography } from 'components/SharedStyledElements';

// types
import { Moment } from 'moment';
import InAppIntegrationsState from 'ducks/merchantDucks/inAppIntegrations/types';
import { ItemAccessTypes } from 'ducks/merchantDucks/items/actions';
import { getFeatures } from 'ducks/merchantDucks/user/selectors';
import { FeatureNames } from 'ducks/merchantDucks/user/types';
import { PaymentTypes, AccessTypes } from '../../PriceContainer';

const { Cell, Container } = Grid;

interface Props extends AnalyticsProps {
  accessTypes: ItemAccessTypes;
  paymentType: string;
  accessTypeId: number | string;
  inAppIntegrations: InAppIntegrationsState;
  isEditAccessFeeScreen: boolean;
  selectedPaymentType: string;
  setPaymentType: any;
  selectedAccessTypeId: number | string;
  setAccessTypeId: any;
  editSeasonFeeDisabled: boolean;
  isReadOnly: boolean;
  selectedFeeId: number;
  isTemplate: boolean;
}

const TooltipStyled = styled(Tooltip)`
  margin-left: 0.5rem;
`;

const StyledCell = styled(Cell)`
  margin-bottom: 0.5rem;
`;

// Payment types
export const paymentTypesList = [
  { value: '1', displayName: 'ppv' },
  { value: '2', displayName: 'subscription' },
  { value: '3', displayName: 'season' },
];

const seasonAccessDateFields = [
  {
    name: 'startsAt',
    title: 'Season start',
    tooltipContent: 'Date and time when the season will start.',
  },
  {
    name: 'expiresAt',
    title: 'Season end',
    tooltipContent: 'Date and time when the season will end.',
  },
  {
    name: 'seasonAnchorDate',
    title: 'Next rebill date',
    tooltipContent: ' Date and time for the next rebill of the season subscription.',
  },
];

const GeneralPriceInfo: FunctionComponent<Props> = ({
  accessTypes,
  inAppIntegrations: { amazonInAppKeys, appleInAppKeys, googleInAppKeys, rokuInAppKeys },
  isEditAccessFeeScreen,
  selectedPaymentType,
  setPaymentType,
  selectedAccessTypeId,
  setAccessTypeId,
  editSeasonFeeDisabled,
  isReadOnly,
  selectedFeeId,
  isTemplate,
}) => {
  const isSeasonFeatureEnabled = useSelector(getFeatures).find((x) => x.name === FeatureNames.SEASON)?.enabled;

  const getAccessPeriodList = (type: string) => {
    if (type === PaymentTypes.Ppv) {
      return accessTypes.ppv;
    }
    if (type === PaymentTypes.Season) {
      return accessTypes.season;
    }
    return accessTypes.subscriptions;
  };

  const accessPeriodList = getAccessPeriodList(selectedPaymentType);
  const isSeasonFee = selectedPaymentType === PaymentTypes.Season;

  const handlePaymentTypeChange = (setFieldValue: (name: string, value: any) => void) => (type: string) => {
    setPaymentType(type);
    const typeList = getAccessPeriodList(type);
    setAccessTypeId(typeList[0].value);
    setFieldValue('accessTypeId', typeList[0].value);
  };

  const handleAccessTypeChange = (id: string) => {
    setAccessTypeId(id);
  };

  const getPaymentList = () => {
    if (isSeasonFeatureEnabled && !isTemplate) {
      return paymentTypesList;
    }
    return paymentTypesList.filter((x) => x.displayName !== 'season');
  };

  const renderCopyInAppIdButton = (itemId: number, feeId: number) => {
    return (
      <StyledCell>
        <ClipBoardButton
          tag="copy_inapp_product_id"
          fullWidth
          clipboardText={`${itemId}_${feeId}`}
          tooltipText="Copied!"
          buttonModifiers={['buttonPrimary']}
        >
          Copy in-app product ID
        </ClipBoardButton>
        <TooltipStyled
          tag="tooltip_copy_inapp_product_id"
          placement="right"
          content={`Provides the ID to be entered in the native\n(mobile or TV) ecosystem for products created there.`}
        >
          <MdInfoOutline />
        </TooltipStyled>
      </StyledCell>
    );
  };

  const renderCopyPriceIdButton = (feeId: number) => {
    return (
      <Cell>
        <ClipBoardButton
          tag="copy_price_id"
          fullWidth
          clipboardText={`${feeId}`}
          tooltipText="Copied!"
          buttonModifiers={['buttonPrimary']}
        >
          Copy Price ID
        </ClipBoardButton>
        <TooltipStyled
          tag="tooltip_copy_price_id"
          placement="right"
          content="Provides the ID of the price you are currently viewing"
        >
          <MdInfoOutline />
        </TooltipStyled>
      </Cell>
    );
  };

  const renderAmountFields = () => {
    return (
      <>
        <Cell width={12}>
          <BlueTypography variant="h6">Amount</BlueTypography>
        </Cell>
        <Cell width={6}>
          <Field tag="text_amount" name="amount" component={Input} placeholder="Amount" disabled={isReadOnly} />
          <FieldError name="amount" />
        </Cell>
        <Cell width={6}>
          <Field name="currency">
            {({ field, form, meta }: FieldProps) => (
              <Select
                tag="dropdown_currencies"
                field={field}
                form={form}
                meta={meta}
                value={field.value}
                placeholder="currency"
                options={accessCurrencies}
                disabled={isReadOnly}
              />
            )}
          </Field>
          <FieldError name="currency" />
        </Cell>
      </>
    );
  };

  const renderSeasonDates = () => {
    return seasonAccessDateFields.map(({ name, title, tooltipContent }) => {
      return (
        <Cell width={6} key={name}>
          <Container>
            <Cell width={12}>
              <Typography variant="p">{title}</Typography>
            </Cell>
            <Field name={name}>
              {({ field: childField, form: childForm }: any) => {
                return (
                  <>
                    <Cell width={4} middle>
                      <SingleDayPicker
                        date={childField.value}
                        handleDateChange={async (date: Moment) => {
                          await childForm.setFieldValue(childField.name, date);
                          await childForm.setFieldTouched(childField.name, true);
                        }}
                        disabled={isReadOnly || editSeasonFeeDisabled}
                      />
                    </Cell>
                    <Cell width={4} middle>
                      <TimeHourPicker
                        handleTimeChange={async (time: Moment) => {
                          await childForm.setFieldValue(childField.name, time);
                          await childForm.setFieldTouched(childField.name, true);
                        }}
                        time={childField.value}
                        disabled={isReadOnly || editSeasonFeeDisabled}
                      />
                    </Cell>
                    <Cell width={1} middle>
                      <Container columns={1}>
                        <Cell width={1} middle>
                          <TooltipStyled placement="top" content={tooltipContent}>
                            <MdInfoOutline />
                          </TooltipStyled>
                        </Cell>
                      </Container>
                    </Cell>
                  </>
                );
              }}
            </Field>
          </Container>
          <FieldError name={name} />
        </Cell>
      );
    });
  };

  const renderSeasonFields = () => {
    return (
      <>
        {!isEditAccessFeeScreen && (
          <>
            <Cell width={12}>
              <BlueTypography variant="h6">Pricing</BlueTypography>
            </Cell>
            <Cell width={12}>
              <span>Next rebill price</span>
              <TooltipStyled
                tag="tooltip_next_rebill_price"
                placement="right"
                content="The price customers will pay upon the next rebill."
              >
                <MdInfoOutline />
              </TooltipStyled>
            </Cell>
            <Cell width={6}>
              <Field
                tag="text_next_rebill_amount"
                name="seasonPriceAmount"
                component={Input}
                placeholder="Amount"
                disabled={isReadOnly || editSeasonFeeDisabled}
              />
              <FieldError name="seasonPriceAmount" />
            </Cell>
            <Cell width={6}>
              <Field name="currency">
                {({ field, form, meta }: FieldProps) => (
                  <Select
                    tag="dropdown_next_rebill_currency"
                    field={field}
                    form={form}
                    meta={meta}
                    value={field.value}
                    placeholder="currency"
                    options={accessCurrencies}
                    disabled={isReadOnly || editSeasonFeeDisabled}
                  />
                )}
              </Field>
              <FieldError name="currency" />
            </Cell>
            <Cell width={12}>
              <span>Current price</span>
              <TooltipStyled
                tag="tooltip_current_rebill_price"
                placement="right"
                content={`The price customers will pay for the current season, if they subscribe right now.\n This is not the price they will pay upon their next rebill.
            `}
              >
                <MdInfoOutline />
              </TooltipStyled>
            </Cell>
            <Cell width={6}>
              <Field
                tag="text_current_amount"
                name="amount"
                component={Input}
                placeholder="Amount"
                disabled={isReadOnly}
              />
              <FieldError name="amount" />
            </Cell>
            <Cell width={6}>
              <Field name="currency">
                {({ field, form, meta }: FieldProps) => (
                  <Select
                    tag="dropdown_current_currency"
                    field={field}
                    form={form}
                    meta={meta}
                    value={field.value}
                    placeholder="currency"
                    options={accessCurrencies}
                    disabled={isReadOnly}
                  />
                )}
              </Field>
              <FieldError name="currency" />
            </Cell>
          </>
        )}
        <Cell width={12}>
          <BlueTypography variant="h6">Off-season access</BlueTypography>
        </Cell>
        <Cell width={12}>
          <Field name="offSeasonAccess" isInlineLabelField>
            {({ field, form }: any) => {
              const {
                values: { feeId },
              } = form;
              return (
                <CheckboxField
                  tag="checkbox_off_season_access"
                  id="offSeasonAccess"
                  name="offSeasonAccess"
                  label="Enable access to content in the off-season period (between season end and the next rebill date)."
                  inlineLabel
                  checked={field.value}
                  onCheck={(val) => form.setFieldValue('offSeasonAccess', val)}
                  disabled={isReadOnly || feeId !== 0 || (isSeasonFee && isEditAccessFeeScreen)}
                />
              );
            }}
          </Field>
        </Cell>
      </>
    );
  };
  // display copy buttons
  const showCopyPriceIdButton = isEditAccessFeeScreen;
  const showCopyInappIdButton =
    isEditAccessFeeScreen &&
    (!isEmpty(amazonInAppKeys) || !isEmpty(appleInAppKeys) || !isEmpty(googleInAppKeys) || !isEmpty(rokuInAppKeys));

  const isCustomPpv = selectedAccessTypeId === AccessTypes.CustomPpv || selectedAccessTypeId === 21;

  return (
    <>
      <StyledContainer margin="2rem 0 0 0" rowGap="1rem" columns={12}>
        <Cell width={12}>
          <BlueTypography variant="h6">Price description</BlueTypography>
        </Cell>
        <Cell width={12}>
          <Field
            tag="text_price_description"
            name="accessFeeDescription"
            type="text"
            component={Input}
            placeholder="Price description"
            disabled={isReadOnly}
          />
          <FieldError name="accessFeeDescription" />
        </Cell>
        <Cell width={12}>
          <BlueTypography variant="h6">Payment type</BlueTypography>
        </Cell>
        <Cell width={6}>
          <Field name="paymentType">
            {({ field, form, meta }: FieldProps) => {
              const {
                values: { feeId },
              } = form;

              return (
                <Select
                  tag="dropdown_payment_type"
                  field={field}
                  form={form}
                  meta={meta}
                  value={selectedPaymentType}
                  placeholder="Select payment type"
                  options={getPaymentList()}
                  handleChange={handlePaymentTypeChange(form.setFieldValue)}
                  // When adding new price feeId is 0
                  disabled={isReadOnly || feeId !== 0 || (isSeasonFee && isEditAccessFeeScreen)}
                />
              );
            }}
          </Field>
          <FieldError name="paymentType" />
        </Cell>
        {!isSeasonFee ? (
          <Cell width={6}>
            <Field name="accessTypeId">
              {({ form, field, meta }: FieldProps) => (
                <Select
                  tag="dropdown_cycles"
                  form={form}
                  field={field}
                  meta={meta}
                  value={field.value}
                  placeholder="Select access period"
                  options={accessPeriodList}
                  handleChange={handleAccessTypeChange}
                  disabled={isReadOnly}
                />
              )}
            </Field>
            <FieldError name="accessTypeId" />
          </Cell>
        ) : (
          !isEditAccessFeeScreen && (
            <>
              <Cell width={12}>
                <BlueTypography variant="h6">Season duration</BlueTypography>
              </Cell>
              {renderSeasonDates()}
            </>
          )
        )}

        {isSeasonFee ? renderSeasonFields() : renderAmountFields()}
        {isSeasonFee && isEditAccessFeeScreen && <SeasonSubscriptionInfo isReadOnly={isReadOnly} />}
        {isCustomPpv && (
          <>
            <Cell width={12}>
              <BlueTypography variant="h6">Expires at</BlueTypography>
            </Cell>
            <Cell width={12}>
              <Container columns={12}>
                <Field name="expiresAt">
                  {({ field, form }: any) => {
                    return (
                      <>
                        <Cell width={2} middle>
                          <SingleDayPicker
                            date={field.value}
                            handleDateChange={(date: Moment) => {
                              form.setFieldValue(field.name, date);
                            }}
                            disabled={isReadOnly}
                          />
                        </Cell>
                        <Cell width={2} middle>
                          <TimeHourPicker
                            key={1}
                            handleTimeChange={(time: Moment) => {
                              form.setFieldValue(field.name, time);
                            }}
                            time={field.value}
                            disabled={isReadOnly}
                          />
                        </Cell>
                      </>
                    );
                  }}
                </Field>
                <Restricted isRestricted={isReadOnly}>
                  <Cell width={1} middle>
                    <Container columns={1}>
                      <Cell width={1} middle>
                        <TooltipStyled
                          placement="top"
                          content={`Date and time when the access for this price option will expire.\nAfter the expiration, the price option will no longer be available for purchase.\nPlease enter your local time.`}
                        >
                          <MdInfoOutline />
                        </TooltipStyled>
                      </Cell>
                    </Container>
                  </Cell>
                </Restricted>
              </Container>
              <FieldError name="expiresAt" />
            </Cell>
          </>
        )}
      </StyledContainer>
      <Field name="selectedFeeId">
        {({ form }: any) => {
          const {
            values: { feeId, itemId },
          } = form;
          return (
            <StyledContainer margin="2rem 0 0 0" columns={1}>
              {showCopyPriceIdButton && renderCopyPriceIdButton(feeId || selectedFeeId)}
              {showCopyInappIdButton && renderCopyInAppIdButton(itemId, feeId || selectedFeeId)}
            </StyledContainer>
          );
        }}
      </Field>
    </>
  );
};

export default GeneralPriceInfo;
